/**
 * GIWAHS Adaptive Journey Core Engine — JourneyRegistry (AJIP-16.02.003 · WS2 · WS6)
 * ---------------------------------------------------------------------
 * Authoritative catalogue of registered journey definitions.
 *
 * Per Founder Ruling 03 — the registry does not hard-code any journey
 * types. Any future journey registers itself through configuration by
 * calling register(definition). The six canonical journeys shipped in
 * this package are initial registrations, not architectural constants.
 *
 * The registry is a Map keyed by definition.identifier. Duplicate
 * identifiers are rejected. Frozen definitions cannot be mutated.
 */
import { freezeDefinition } from './JourneyConfig';
import { InvalidRequestError, NotRegisteredError, ConfigError } from './JourneyErrors';
import { assertNoDuplicateTransitions } from './TransitionEngine';

class JourneyRegistry {
  constructor() {
    this._entries = new Map();
  }

  register(definition) {
    const frozen = freezeDefinition(definition);
    if (this._entries.has(frozen.identifier)) {
      throw new ConfigError(`Journey '${frozen.identifier}' already registered`);
    }
    assertNoDuplicateTransitions(frozen.transitions, frozen.identifier);
    this._entries.set(frozen.identifier, frozen);
    return frozen;
  }

  unregister(identifier) {
    if (typeof identifier !== 'string') {
      throw new InvalidRequestError('identifier must be a string');
    }
    return this._entries.delete(identifier);
  }

  has(identifier) {
    return this._entries.has(identifier);
  }

  get(identifier) {
    if (!this._entries.has(identifier)) {
      throw new NotRegisteredError(`Journey '${identifier}' is not registered`);
    }
    return this._entries.get(identifier);
  }

  list() {
    return Object.freeze(Array.from(this._entries.values()));
  }

  ids() {
    return Object.freeze(Array.from(this._entries.keys()));
  }

  size() {
    return this._entries.size;
  }

  clear() {
    this._entries.clear();
  }
}

export { JourneyRegistry };
